import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Images, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * "Save to Stock" for an article's scraped source images — used on the
 * featured-image panel's "From source articles" grid and on the body-
 * image modal's "From source" tab.
 *
 * The photo is fetched server-side (the URL must be one of this
 * article's `source_images`) and saved to Newsdesk Stock as ACTIVE
 * straight away — unlike the upload checkbox, it isn't tied to
 * publishing, because the editor chose it explicitly. The library keeps
 * the source URL so the photo's origin stays visible.
 *
 *   const srcStock = useSourceStock(article);
 *   <SourceStockButton srcStock={srcStock} url={u} defaultCredit="Photo courtesy of …" />
 */
export function useSourceStock(article) {
  const status = useQuery({
    queryKey: ["stock-library-status"],
    queryFn: api.stockLibraryStatus,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const configured = !!status.data?.configured;
  const hasSources = (article?.source_images || []).length > 0;
  const saved = useQuery({
    queryKey: ["stock-source-status", article?.id],
    queryFn: () => api.stockSourceStatus(article.id),
    enabled: configured && hasSources && !!article?.id,
    staleTime: 60 * 1000,
    retry: false,
  });
  return {
    article,
    configured,
    savedMap: saved.data?.saved || {},
  };
}

export function SourceStockButton({ srcStock, url, defaultCredit = "", className = "" }) {
  const [open, setOpen] = useState(false);
  if (!srcStock?.configured) return null;
  const inStock = !!srcStock.savedMap[url];
  if (inStock) {
    return (
      <span
        data-testid="source-in-stock"
        title="Already in Newsdesk Stock"
        className={
          "inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-black/45 text-white " + className
        }
      >
        <Check className="w-3 h-3" /> In Stock
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        data-testid="source-save-to-stock"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
        title="Save this source image to Newsdesk Stock"
        className={
          "inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-black/45 text-white hover:bg-black/65 " +
          className
        }
      >
        <Images className="w-3 h-3" /> Save to Stock
      </button>
      {open && (
        <SaveSourceDialog
          article={srcStock.article}
          url={url}
          defaultCredit={defaultCredit}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function SaveSourceDialog({ article, url, defaultCredit, onClose }) {
  const qc = useQueryClient();
  const [keywords, setKeywords] = useState("");
  const [credit, setCredit] = useState(defaultCredit || "");
  const [title, setTitle] = useState("");

  const save = useMutation({
    mutationFn: () =>
      api.addSourceImageToStock(article.id, url, { keywords, credit, title }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["stock-source-status", article.id] });
      qc.invalidateQueries({ queryKey: ["stock-library"] });
      toast.success(
        res?.duplicate ? "This photo is already in Newsdesk Stock" : "Saved to Newsdesk Stock",
      );
      onClose();
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || e?.message || "Could not save to Newsdesk Stock"),
  });

  // Rendered inside other clickable tiles and modals: every click here
  // stops propagating so it never triggers "use as featured" / "insert",
  // or closes the body-image modal underneath.
  const stop = (e) => e.stopPropagation();

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center px-4"
      onClick={(e) => {
        stop(e);
        if (!save.isPending) onClose();
      }}
    >
      <div
        onClick={stop}
        data-testid="source-stock-dialog"
        className="w-full max-w-md bg-white border border-[var(--border)] shadow-xl text-left"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
          <div>
            <div className="overline">Newsdesk Stock</div>
            <h2 className="font-serif-display text-lg">Save source image</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--ink-muted)] hover:text-[var(--ink)]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          <div className="aspect-video bg-[var(--secondary)] border border-[var(--border)] overflow-hidden">
            <img src={url} alt="" className="w-full h-full object-contain" />
          </div>
          <input
            data-testid="source-stock-keywords"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="Keywords, comma separated"
            autoFocus
            className="w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
          />
          <input
            data-testid="source-stock-credit"
            value={credit}
            onChange={(e) => setCredit(e.target.value)}
            placeholder="Preset caption / credit (e.g. Photo courtesy of …)"
            className="w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
          />
          <input
            data-testid="source-stock-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            className="w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
          />
          <div className="text-[10px] text-[var(--ink-muted)]">
            Source photos usually belong to someone else — keep the credit accurate and
            use them the way the source allows. The library remembers where it came from.
          </div>
        </div>
        <div className="px-4 py-3 border-t border-[var(--border)] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={save.isPending}
            className="text-xs px-3 py-1.5 border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="source-stock-save"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--ink)] text-white hover:opacity-90 disabled:opacity-40"
          >
            {save.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Images className="w-3.5 h-3.5" />}
            Save to Stock
          </button>
        </div>
      </div>
    </div>
  );
}
